// src/utils/fileTypeUtils.ts
export interface FileTypeInfo {
  text: string;
  backgroundColor: string;
  color: string;
  icon: string;
}

/**
 * MIMEタイプとファイル名からファイルタイプ情報を取得する
 * @param mimeType MIMEタイプ
 * @param fileName ファイル名（オプション、フォールバック用）
 * @returns ファイルタイプ情報
 */
export const getFileTypeInfo = (mimeType: string, fileName?: string): FileTypeInfo => {
  // 1. 画像ファイル（最優先）
  if (mimeType.startsWith('image/')) {
    return { text: 'IMG', backgroundColor: '#718096', color: 'white', icon: '🖼️' };
  }

  // 2. 動画ファイル
  if (mimeType.startsWith('video/')) {
    return { text: 'VID', backgroundColor: '#4a5568', color: 'white', icon: '🎬' };
  }

  // 2.5. 音声ファイル
  if (mimeType.startsWith('audio/')) {
    return { text: 'AUD', backgroundColor: '#805ad5', color: 'white', icon: '🎵' };
  }

  // 3. PDF（具体的なMIMEタイプ）
  if (mimeType === 'application/pdf') {
    return { text: 'PDF', backgroundColor: '#e15554', color: 'white', icon: '📄' };
  }

  // 3. Microsoft Office ファイル（具体的なMIMEタイプで判定）
  if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    return { text: 'DOCX', backgroundColor: '#2b67c6', color: 'white', icon: '📝' };
  }
  
  if (mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
    return { text: 'PPTX', backgroundColor: '#d04424', color: 'white', icon: '📽️' };
  }
  
  if (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
    return { text: 'XLSX', backgroundColor: '#1b8755', color: 'white', icon: '📊' };
  }

  // 4. レガシーOfficeファイル
  if (mimeType === 'application/msword') {
    return { text: 'DOC', backgroundColor: '#2b67c6', color: 'white', icon: '📝' };
  }
  
  if (mimeType === 'application/vnd.ms-powerpoint') {
    return { text: 'PPT', backgroundColor: '#d04424', color: 'white', icon: '📽️' };
  }
  
  if (mimeType === 'application/vnd.ms-excel') {
    return { text: 'XLS', backgroundColor: '#1b8755', color: 'white', icon: '📊' };
  }

  // 5. テキストファイル
  if (mimeType === 'text/plain') {
    return { text: 'TXT', backgroundColor: '#766a60', color: 'white', icon: '📄' };
  }
  
  if (mimeType === 'text/markdown') {
    return { text: 'MD', backgroundColor: '#766a60', color: 'white', icon: '📄' };
  }
  
  if (mimeType === 'text/csv') {
    return { text: 'CSV', backgroundColor: '#1b8755', color: 'white', icon: '📊' };
  }

  // 6. フォールバック（拡張子ベース）
  if (fileName) {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'mp4':
      case 'mov':
      case 'webm':
        return { text: 'VID', backgroundColor: '#4a5568', color: 'white', icon: '🎬' };
      case 'mp3':
      case 'wav':
      case 'ogg':
      case 'm4a':
      case 'aac':
        return { text: 'AUD', backgroundColor: '#805ad5', color: 'white', icon: '🎵' };
      case 'pdf': return { text: 'PDF', backgroundColor: '#e15554', color: 'white', icon: '📄' };
      case 'docx': return { text: 'DOCX', backgroundColor: '#2b67c6', color: 'white', icon: '📝' };
      case 'pptx': return { text: 'PPTX', backgroundColor: '#d04424', color: 'white', icon: '📽️' };
      case 'xlsx': return { text: 'XLSX', backgroundColor: '#1b8755', color: 'white', icon: '📊' };
      case 'doc': return { text: 'DOC', backgroundColor: '#2b67c6', color: 'white', icon: '📝' };
      case 'ppt': return { text: 'PPT', backgroundColor: '#d04424', color: 'white', icon: '📽️' };
      case 'xls': return { text: 'XLS', backgroundColor: '#1b8755', color: 'white', icon: '📊' };
      case 'txt': return { text: 'TXT', backgroundColor: '#766a60', color: 'white', icon: '📄' };
      case 'md': return { text: 'MD', backgroundColor: '#766a60', color: 'white', icon: '📄' };
      case 'csv': return { text: 'CSV', backgroundColor: '#1b8755', color: 'white', icon: '📊' };
    }
  }

  // 7. デフォルト
  return { text: 'FILE', backgroundColor: '#adb5bd', color: 'white', icon: '📎' };
};

/**
 * ファイルタイプフィルター用の判定関数
 * @param mimeType MIMEタイプ
 * @param filterType フィルタータイプ
 * @returns フィルターにマッチするかどうか
 */
export const matchesFileTypeFilter = (mimeType: string, filterType: string): boolean => {
  if (filterType === 'all') return true;
  
  const fileTypeInfo = getFileTypeInfo(mimeType);
  
  switch (filterType) {
    case 'pdf':
      return fileTypeInfo.text === 'PDF';
    case 'document':
      return ['DOC', 'DOCX'].includes(fileTypeInfo.text);
    case 'spreadsheet':
      return ['XLS', 'XLSX'].includes(fileTypeInfo.text);
    case 'presentation':
      return ['PPT', 'PPTX'].includes(fileTypeInfo.text);
    case 'text':
      return ['TXT', 'MD', 'CSV'].includes(fileTypeInfo.text);
    case 'image':
      return fileTypeInfo.text === 'IMG';
    case 'other':
      return fileTypeInfo.text === 'FILE';
    default:
      return true;
  }
};
